import React, { useState } from "react";
import { Badge } from "react-bootstrap";
import {
    FiCheckCircle,
    FiClock,
    FiChevronDown,
    FiChevronUp,
    FiCalendar,
    FiMessageSquare
} from "react-icons/fi";
import moment from "moment-timezone";

const RecentUpdatesBadge = ({ getRecentUpdates }) => {
    const { todayUpdates, allUpdates } = getRecentUpdates;
    const hasTodayUpdates = todayUpdates.length > 0;
    const updatesToShow = hasTodayUpdates ? todayUpdates : allUpdates.slice(0, 5);
    const [isCollapsed, setIsCollapsed] = useState(true);

    if (updatesToShow.length === 0) return null;

    const statuses = ["Paid", "Pending", "Unpaid"];

    const statusColors = {
        Paid: { text: "#16a34a", bg: "#16a34a20" },
        Pending: { text: "#d97706", bg: "#d9770620" },
        Unpaid: { text: "#dc2626", bg: "#dc262620" }
    };

    const formatDisplayDate = (str) => {
        const [date, time] = str.split(", ");
        return { date, time: time || "" };
    };

    const groupedUpdates = {};
    updatesToShow.forEach((u) => {
        const { date } = formatDisplayDate(u.updated_time);
        if (!groupedUpdates[date]) groupedUpdates[date] = [];
        groupedUpdates[date].push(u);
    });

    const getDateColor = (str) => {
        const d = moment(str, "DD MMM YYYY");
        const today = moment().startOf("day");
        const diff = today.diff(d, "days");

        if (diff === 0) return { bg: "#ECFDF5", text: "#047857" };
        if (diff === 1) return { bg: "#EFF6FF", text: "#1D4ED8" };
        if (diff <= 7) return { bg: "#FFFBEB", text: "#B45309" };
        return { bg: "#F3F4F6", text: "#4B5563" };
    };
    const statusCounts = statuses.reduce((acc, s) => {
        acc[s] = updatesToShow.filter(u => u.status === s).length;
        return acc;
    }, {});

    return (
        <div
            className="mt-3 mb-2 rounded shadow-sm"
            style={{ background: "#fff", border: "1px solid #e5e7eb" }}
        >
            <div
                className="d-flex align-items-center justify-content-between px-3 py-3"
                style={{
                    cursor: 'pointer',
                    backgroundColor: '#fbffd3ff'
                }}
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="d-flex align-items-center">
                    {hasTodayUpdates ? (
                        <FiCheckCircle size={14} className="text-success me-2" />
                    ) : (
                        <FiClock size={14} className="text-secondary me-2" />
                    )}
                    <span className="fw-semibold" style={{ fontSize: 12 }}>
                        {hasTodayUpdates ? "Today's Updates" : "Recent Updates"}{" "}
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            ({updatesToShow.length})
                        </span>
                    </span>

                    <div className="d-flex justify-content-between align-items-center ms-2" style={{ fontSize: "12px" }}>
                        <div className="d-flex gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: "10px",
                                    height: "10px",
                                    backgroundColor: "#16a34a",
                                    borderRadius: "50%"
                                }}></div>
                                <span className="text-muted">
                                    <span className="fw-bold">{statusCounts.Paid}</span> Paid
                                </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: "10px",
                                    height: "10px",
                                    backgroundColor: "#d97706",
                                    borderRadius: "50%"
                                }}></div>
                                <span className="text-muted">
                                    <span className="fw-bold">{statusCounts.Pending}</span> Pending
                                </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: "10px",
                                    height: "10px",
                                    backgroundColor: "#dc2626",
                                    borderRadius: "50%"
                                }}></div>
                                <span className="text-muted">
                                    <span className="fw-bold">{statusCounts.Unpaid}</span> Unpaid
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <FiChevronDown
                    size={18}
                    className={`text-muted transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                    style={{
                        transition: 'transform 0.3s ease',
                        transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                    }}
                />
            </div>

            {!isCollapsed && (
                <div className="px-3 pt-4">
                    {Object.entries(groupedUpdates).map(([date, list]) => {
                        const c = getDateColor(date);

                        return (
                            <div key={date} className="mb-4">
                                <div
                                    className="d-inline-flex align-items-center px-3 py-1 rounded-pill mb-2"
                                    style={{
                                        background: c.bg,
                                        color: c.text,
                                        fontWeight: 600,
                                        fontSize: 12
                                    }}
                                >
                                    <FiCalendar size={13} className="me-2" />
                                    {date} • {list.length} update{list.length > 1 ? "s" : ""}
                                </div>

                                <div className="row g-2">
                                    {list.map((u, idx) => {
                                        const st = statusColors[u.status];

                                        return (
                                            <div key={idx} className="col-12 col-md-6 col-lg-3">
                                                <div
                                                    className="p-3 rounded"
                                                    style={{
                                                        border: "1px solid #e5e7eb",
                                                        background: "#fafafa"
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span
                                                            className="fw-semibold text-truncate"
                                                            title={u.customerName}
                                                            style={{ fontSize: 14 }}
                                                        >
                                                            {u.customerName}
                                                        </span>

                                                        <span
                                                            className="px-2 py-1 rounded-pill"
                                                            style={{
                                                                background: st.bg,
                                                                color: st.text,
                                                                fontSize: 11,
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {u.status}
                                                        </span>
                                                    </div>

                                                    {u.comments && (
                                                        <div
                                                            className="text-muted mb-2 text-truncate"
                                                            title={u.comments}
                                                            style={{ fontSize: 12 }}
                                                        >
                                                            <FiMessageSquare size={12} className="me-1" />
                                                            {u.comments}
                                                        </div>
                                                    )}

                                                    <div
                                                        className="d-flex justify-content-between text-muted"
                                                        style={{ fontSize: 12 }}
                                                    >
                                                        <span>
                                                            <FiClock size={12} className="me-1" />
                                                            {u.updated_time.split(", ")[1]}
                                                        </span>
                                                        <span>
                                                            <FiCalendar size={12} className="me-1" />
                                                            {u.month}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RecentUpdatesBadge;
